(() => {
  'use strict';
  const active = new WeakMap();

  function initialize(root) {
    if (active.has(root)) return;
    const lifecycle = new AbortController();
    active.set(root, lifecycle);
    const listener = { signal: lifecycle.signal };
    const get = name => root.querySelector(`[data-rack-${name}]`);
    const surface = get('surface');
    const items = get('items');
    const open = get('open');
    const close = get('close');
    const details = get('details');
    const previous = get('prev');
    const next = get('next');
    let products = [];
    let nodes = [];
    let position = 0;
    let lastFocus = null;
    let touch = null;
    let suppressClick = false;
    let suppressTimer = null;
    let activeUpgrade = null;
    let galleryUrls = [];
    let galleryPreviewTimer = null;
    let galleryPreviewVersion = 0;
    let factsTimer = null;
    let exitTimer = null;
    let detailTransitionTimer = null;
    let detailTransitionStartedAt = 0;
    let closingCorrectionTimer = null;
    let hasSelected = false;
    const swayAnimations = new Map();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let rackInView = typeof IntersectionObserver !== 'function';
    let revealBreezePlayed = false;

    function swayAllowed() {
      if (lifecycle.signal.aborted || !rackInView || document.hidden || reducedMotion?.matches) return false;
      const preference = document.documentElement.dataset.nlaProductMotion;
      if (preference) return preference === 'enabled';
      try { return sessionStorage.getItem('nla-product-motion-paused') !== 'true'; } catch (_) { return true; }
    }

    function stopFabricSway(fabric) {
      const animation = swayAnimations.get(fabric);
      swayAnimations.delete(fabric);
      animation?.cancel();
      fabric?.style.removeProperty('will-change');
    }

    function stopSway() {
      [...swayAnimations.keys()].forEach(stopFabricSway);
    }

    function swayClothes(direction = 1, strength = 1) {
      for (const fabric of swayAnimations.keys()) {
        const item = fabric.closest('.nla-rack__item');
        if (!item || item.hidden || item.classList.contains('is-photo') || item.classList.contains('is-missing-image') || Number(item.dataset.rackRank) > 2) stopFabricSway(fabric);
      }
      if (!swayAllowed()) { stopSway(); return; }
      revealBreezePlayed = true;
      nodes.forEach(node => {
        const rank = Number(node.dataset.rackRank);
        const fabric = node.querySelector('.nla-rack__fabric');
        if (!fabric?.animate || node.hidden || node.getAttribute('aria-hidden') !== 'false' || rank > 2 || node.classList.contains('is-photo') || node.classList.contains('is-pending-image')) return;
        let angle = 0;
        if (swayAnimations.has(fabric)) {
          const matrix = new DOMMatrixReadOnly(getComputedStyle(fabric).transform);
          angle = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
        }
        stopFabricSway(fabric);
        const amplitude = (rank === 0 ? 1.65 : .8 / rank) * strength * (node.classList.contains('has-own-hanger') ? .65 : 1);
        const impulse = direction * amplitude;
        // The cloth first trails the moving rail, then carries past its stop.
        // Only this fabric layer moves: the hook and the hit target stay aligned.
        const easing = 'cubic-bezier(.45,0,.3,1)';
        const keyframes = [
          {offset:0, transform:`rotate(${angle}deg)`, easing},
          {offset:.18, transform:`rotate(${-impulse * .5}deg)`, easing},
          {offset:.43, transform:`rotate(${impulse}deg)`, easing},
          {offset:.63, transform:`rotate(${-impulse * .42}deg)`, easing},
          {offset:.8, transform:`rotate(${impulse * .15}deg)`, easing},
          {offset:.92, transform:`rotate(${-impulse * .04}deg)`, easing},
          {offset:1, transform:'rotate(0deg)'}
        ];
        fabric.style.willChange = 'transform';
        const animation = fabric.animate(keyframes, {duration:1600 + rank * 70, fill:'none'});
        swayAnimations.set(fabric, animation);
        const finish = () => {
          if (swayAnimations.get(fabric) !== animation) return;
          swayAnimations.delete(fabric);
          fabric.style.removeProperty('will-change');
        };
        animation.onfinish = animation.oncancel = finish;
      });
    }

    function revealBreeze() {
      if (revealBreezePlayed || !hasSelected || !swayAllowed()) return;
      if (!nodes[position] || nodes[position].classList.contains('is-pending-image')) return;
      revealBreezePlayed = true;
      swayClothes(1, .45);
    }

    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(entries => {
        rackInView = entries.some(entry => entry.isIntersecting);
        if (rackInView) revealBreeze(); else stopSway();
      });
      observer.observe(get('area') || root);
      lifecycle.signal.addEventListener('abort', () => observer.disconnect(), {once:true});
    }
    const pauseObserver = new MutationObserver(() => { if (!swayAllowed()) stopSway(); });
    pauseObserver.observe(document.documentElement, {attributes:true, attributeFilter:['data-nla-product-motion']});
    reducedMotion?.addEventListener?.('change', () => { if (!swayAllowed()) stopSway(); }, listener);
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopSway(); }, listener);
    lifecycle.signal.addEventListener('abort', () => { pauseObserver.disconnect(); stopSway(); }, {once:true});

    get('title').textContent = root.dataset.loading;
    get('type').textContent = '';
    get('price').textContent = '';
    get('position').textContent = '';
    get('count').textContent = '';
    get('missing').hidden = true;
    get('link').hidden = true;
    if (get('facts')) get('facts').hidden = true;
    open.disabled = previous.disabled = next.disabled = true;
    details.inert = true;
    details.setAttribute('aria-hidden', 'true');
    open.setAttribute('aria-expanded', 'false');
    surface.classList.remove('is-open', 'is-photo', 'is-square-active', 'is-own-hanger');
    items.replaceChildren();
    lifecycle.signal.addEventListener('abort', cancelGalleryPreviews, { once: true });
    lifecycle.signal.addEventListener('abort', cancelFacts, { once: true });

    function closeDetails(restoreFocus = true) {
      cancelGalleryPreviews();
      cancelFacts();
      startDetailTransition();
      applyRackAlignment(false);
      surface.classList.remove('is-open');
      details.inert = true;
      details.setAttribute('aria-hidden', 'true');
      open.setAttribute('aria-expanded', 'false');
      if (restoreFocus) {
        const target = lastFocus?.isConnected && !lastFocus.hidden && lastFocus.getAttribute('aria-hidden') !== 'true' ? lastFocus : open;
        target.focus();
      }
      lastFocus = null;
      swayClothes(-1, .6);
    }

    function openDetails() {
      if (!products[position]) return;
      lastFocus = document.activeElement;
      renderGallery(products[position]);
      queueFacts(products[position]);
      startDetailTransition();
      applyRackAlignment(true);
      details.inert = false;
      details.setAttribute('aria-hidden', 'false');
      open.setAttribute('aria-expanded', 'true');
      surface.classList.add('is-open');
      close.focus();
      swayClothes(1, .7);
    }

    function startDetailTransition() {
      clearTimeout(detailTransitionTimer);
      detailTransitionStartedAt = performance.now();
      surface.classList.add('is-detail-transitioning');
      detailTransitionTimer = setTimeout(() => { surface.classList.remove('is-detail-transitioning'); detailTransitionTimer = null; }, 950);
    }

    function cancelClosingCorrection() {
      clearTimeout(closingCorrectionTimer);
      closingCorrectionTimer = null;
      nodes.forEach(node => node.classList.remove('is-closing-adjusted'));
    }

    function renderFacts(product) {
      const facts = get('facts');
      if (!facts) return;
      const description = get('description');
      const variants = get('variants');
      description.textContent = product.description;
      variants.textContent = product.variants.join(' · ');
      get('description-block').hidden = !product.description;
      get('variants-block').hidden = !product.variants.length;
      facts.hidden = !product.description && !product.variants.length;
    }

    function cancelFacts() {
      clearTimeout(factsTimer);
      factsTimer = null;
      if (get('facts')) { get('facts').hidden = true; get('facts').classList.remove('is-pending'); }
    }

    function queueFacts(product) {
      cancelFacts();
      if (!get('facts') || (!product.description && !product.variants.length)) return;
      renderFacts(product);
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      // Reserve the text's space immediately, then reveal it after the rail settles.
      get('facts').classList.add('is-pending');
      factsTimer = setTimeout(() => {
        factsTimer = null;
        if (!lifecycle.signal.aborted && surface.classList.contains('is-open') && products[position] === product) get('facts').classList.remove('is-pending');
      }, 950);
    }

    function renderGallery(product) {
      const gallery = get('gallery');
      const image = get('detail-image');
      cancelGalleryPreviews();
      gallery.replaceChildren();
      const urls = product.gallery.length ? product.gallery : product.image ? [product.image] : [];
      galleryUrls = urls;
      image.alt = product.title;
      if (!urls.length) {
        image.hidden = true;
        image.removeAttribute('src');
        get('gallery-status').hidden = true;
        return;
      }
      requestGalleryImage(urls[0]);
      urls.forEach((url, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(index + 1);
        button.dataset.rackGalleryIndex = String(index);
        button.setAttribute('aria-label', `${root.dataset.galleryLabel} ${index + 1}`);
        button.setAttribute('aria-pressed', String(index === 0));
        gallery.append(button);
      });
      const version = galleryPreviewVersion;
      const delay = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 950;
      galleryPreviewTimer = setTimeout(() => {
        galleryPreviewTimer = null;
        if (lifecycle.signal.aborted || version !== galleryPreviewVersion || !surface.classList.contains('is-open')) return;
        [...gallery.querySelectorAll('button[data-rack-gallery-index]')].forEach((button, index) => {
          const fallback = document.createElement('span');
          fallback.textContent = String(index + 1);
          const preview = document.createElement('img');
          preview.alt = '';
          preview.loading = 'lazy';
          preview.decoding = 'async';
          preview.width = preview.height = 160;
          preview.onload = () => {
            const loadedSrc = preview.currentSrc;
            const reveal = () => {
              if (version !== galleryPreviewVersion || button.parentElement !== gallery || !preview.complete || !preview.naturalWidth || preview.currentSrc !== loadedSrc) return;
              preview.classList.add('is-ready');
              fallback.hidden = true;
            };
            if (typeof preview.decode === 'function') preview.decode().then(reveal, reveal);
            else reveal();
          };
          preview.onerror = () => { preview.classList.remove('is-ready'); fallback.hidden = false; };
          button.replaceChildren(preview, fallback);
          preview.src = galleryPreviewUrl(urls[index]);
        });
      }, delay);
    }

    function cancelGalleryPreviews() {
      clearTimeout(galleryPreviewTimer);
      galleryPreviewTimer = null;
      galleryPreviewVersion++;
      get('gallery').querySelectorAll('img').forEach(image => {
        image.onload = image.onerror = null;
        image.removeAttribute('src');
      });
    }

    function galleryPreviewUrl(source) {
      try {
        const url = new URL(source, window.location.href);
        if (/(?:\/cdn\/shop\/files\/|\/s\/files\/[^?#]*\/files\/)[^/]+$/i.test(url.pathname) && url.searchParams.has('width')) {
          url.searchParams.set('width', '160');
          return url.href;
        }
      } catch (_) { /* Use the original source if it cannot be resized. */ }
      return source;
    }

    function requestGalleryImage(url) {
      const image = get('detail-image');
      if (image.getAttribute('src') === url && (!image.complete || (image.naturalWidth && !image.hidden))) return;
      image.hidden = true;
      get('gallery-status').textContent = root.dataset.imageLoading;
      get('gallery-status').hidden = false;
      image.src = url;
    }

    get('detail-image').addEventListener('load', () => {
      const image = get('detail-image');
      if (!image.complete || !image.naturalWidth || image.currentSrc !== image.src) return;
      image.hidden = false;
      get('gallery-status').hidden = true;
    }, listener);
    get('detail-image').addEventListener('error', () => {
      const image = get('detail-image');
      if (!image.getAttribute('src') || !image.complete || image.naturalWidth) return;
      image.hidden = true;
      get('gallery-status').textContent = root.dataset.galleryError;
      get('gallery-status').hidden = false;
    }, listener);

    function updateImageStatus() {
      const product = products[position];
      const node = nodes[position];
      if (!product || !node) return;
      const image = node.querySelector('.nla-rack__shirt');
      const missing = !product.image || node.dataset.imageError === 'true';
      const loading = !missing && (node.classList.contains('is-pending-image') || !image.complete || !image.naturalWidth);
      get('missing').textContent = missing ? root.dataset.missing : root.dataset.imageLoading;
      get('missing').hidden = !(missing || loading);
    }

    get('gallery').addEventListener('click', event => {
      const button = event.target.closest('button[data-rack-gallery-index]');
      if (!button || button.parentElement !== get('gallery')) return;
      const index = Number(button.dataset.rackGalleryIndex);
      if (!galleryUrls[index]) return;
      requestGalleryImage(galleryUrls[index]);
      get('gallery').querySelectorAll('button').forEach((item, n) => item.setAttribute('aria-pressed', String(n === index)));
    }, listener);

    function makeShirtImage(product) {
      const image = document.createElement('img');
      image.className = 'nla-rack__shirt';
      image.alt = '';
      image.decoding = 'async';
      image.dataset.src = product.image;
      return image;
    }

    function bindShirtImage(button, image, product, index) {
      image.addEventListener('load', () => {
        const loadedSrc = image.currentSrc;
        const reveal = () => {
          if (lifecycle.signal.aborted || !button.contains(image) || image.currentSrc !== loadedSrc || !image.complete || !image.naturalWidth) return;
          button.classList.remove('is-pending-image');
          const wasSquare = button.classList.contains('is-square-image');
          button.classList.toggle('is-square-image', image.naturalWidth === image.naturalHeight);
          if (index === position) { syncActiveImageFormat(); updateImageStatus(); }
          revealBreeze();
          if (wasSquare !== button.classList.contains('is-square-image') && button.getAttribute('aria-hidden') === 'false') applyRackAlignment(surface.classList.contains('is-open'));
        };
        if (typeof image.decode === 'function') image.decode().then(reveal, reveal);
        else reveal();
      }, listener);
      image.addEventListener('error', () => {
        button.classList.add('is-pending-image');
        const highResolutionUrl = stageImageUrl(product, true);
        if (!button.dataset.highResolutionFailed && highResolutionUrl !== product.image && image.src === highResolutionUrl) {
          button.dataset.highResolutionFailed = 'true';
          image.src = product.image;
          if (index === position) updateImageStatus();
          return;
        }
        if (product.fallbackImage && !button.dataset.fallbackTried) {
          button.dataset.fallbackTried = 'true';
          product.image = product.fallbackImage;
          product.imageKind = 'photo';
          product.ownHanger = false;
          stopFabricSway(button.querySelector('.nla-rack__fabric'));
          button.classList.add('is-photo');
          button.classList.remove('has-own-hanger', 'is-square-image');
          image.src = product.fallbackImage;
          select(position);
          return;
        }
        button.dataset.imageError = 'true';
        stopFabricSway(button.querySelector('.nla-rack__fabric'));
        button.classList.add('is-missing-image');
        image.hidden = true;
        if (index === position) updateImageStatus();
      }, listener);
    }

    function createItem(product, index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nla-rack__item is-pending-image';
      button.hidden = true;
      button.setAttribute('aria-label', product.title);
      if (product.imageKind === 'photo') button.classList.add('is-photo');
      if (!product.image) button.classList.add('is-missing-image');
      if (product.imageShape === 'square') button.classList.add('is-square-image');
      if (product.ownHanger) button.classList.add('has-own-hanger');
      const hanger = document.createElement('img');
      hanger.className = 'nla-rack__hanger';
      hanger.alt = '';
      hanger.src = root.dataset.hanger;
      hanger.decoding = 'async';
      const hangerBox = document.createElement('span');
      hangerBox.className = 'nla-rack__hanger-box';
      hangerBox.setAttribute('aria-hidden', 'true');
      hangerBox.append(hanger);
      const image = makeShirtImage(product);
      bindShirtImage(button, image, product, index);
      const fabric = document.createElement('span');
      fabric.className = 'nla-rack__fabric';
      fabric.setAttribute('aria-hidden', 'true');
      fabric.append(image);
      const tapzone = document.createElement('span');
      tapzone.className = 'nla-rack__tapzone';
      tapzone.setAttribute('aria-hidden', 'true');
      button.append(hangerBox, fabric, tapzone);
      button.addEventListener('click', event => {
        if (suppressClick) { event.preventDefault(); suppressClick = false; return; }
        if (index === position) openDetails();
        else select(index);
      }, listener);
      items.append(button);
      return button;
    }

    function stageImageUrl(product, selected) {
      if (!selected || product.imageKind !== 'cutout' || !product.image) return product.image;
      try {
        const url = new URL(product.image, window.location.href);
        if (/(?:\/cdn\/shop\/files\/|\/s\/files\/[^?#]*\/files\/)[^/]+$/i.test(url.pathname) && url.searchParams.get('width') === '480') {
          url.searchParams.set('width', '1000');
          return url.href;
        }
      } catch (_) { /* Leave the original image URL unchanged. */ }
      return product.image;
    }

    function upgradeShirtImage(button, image, product, index, desired) {
      if (button.dataset.highResolutionPending || button.dataset.highResolutionFailed) return;
      const original = product.image;
      let upgrade = null;
      let cancelled = false;
      let timer = null;
      button.dataset.highResolutionPending = 'true';
      const cleanup = () => {
        lifecycle.signal.removeEventListener('abort', cancel);
        if (activeUpgrade?.cancel === cancel) {
          delete button.dataset.highResolutionPending;
          activeUpgrade = null;
        }
      };
      const cancel = () => {
        cancelled = true;
        clearTimeout(timer);
        if (upgrade) { upgrade.onload = null; upgrade.onerror = null; upgrade.removeAttribute('src'); }
        cleanup();
      };
      const succeed = () => {
        cleanup();
        if (cancelled || lifecycle.signal.aborted || index !== position || !button.isConnected || product.image !== original || button.querySelector('.nla-rack__shirt') !== image) return;
        bindShirtImage(button, upgrade, product, index);
        image.replaceWith(upgrade);
        button.classList.remove('is-missing-image', 'is-pending-image');
        delete button.dataset.imageError;
        button.classList.toggle('is-square-image', upgrade.naturalWidth === upgrade.naturalHeight);
        if (index === position) { syncActiveImageFormat(); updateImageStatus(); }
      };
      const fail = () => {
        cleanup();
        if (!cancelled && !lifecycle.signal.aborted && product.image === original) button.dataset.highResolutionFailed = 'true';
      };
      activeUpgrade = { index, cancel };
      lifecycle.signal.addEventListener('abort', cancel, { once: true });
      timer = setTimeout(() => {
        if (cancelled || lifecycle.signal.aborted || index !== position || !button.isConnected || product.image !== original || button.querySelector('.nla-rack__shirt') !== image) { cancel(); return; }
        upgrade = makeShirtImage(product);
        if (typeof upgrade.decode === 'function') {
          upgrade.src = desired;
          upgrade.decode().then(succeed, fail);
        } else {
          upgrade.onload = succeed;
          upgrade.onerror = fail;
          upgrade.src = desired;
        }
      }, 120);
    }

    function syncActiveImageFormat() {
      const product = products[position];
      const square = nodes[position]?.classList.contains('is-square-image');
      surface.classList.toggle('is-square-active', Boolean(square && product && product.imageKind === 'cutout' && !product.ownHanger));
    }

    // Calculate both resting positions from the rail and the current responsive stage size.
    function rackMetrics(product) {
      const width = surface.clientWidth;
      const narrow = window.matchMedia?.('(max-width: 900px)').matches;
      const main = surface.querySelector('.nla-rack__main');
      if (!width || !main) return null;
      const activeSquare = product.imageKind === 'cutout' && !product.ownHanger && nodes[position]?.classList.contains('is-square-image');
      return {width, viewportWidth:window.innerWidth || width, narrow, mainHeight:main.clientHeight, photo:product.imageKind === 'photo', own:product.ownHanger, activeSquare};
    }

    function alignedRackY(node, rank, metrics, opened) {
      if (rank === 0) return 0;
      if (!metrics) return rank * 5.22;
      const {width,viewportWidth,narrow,mainHeight,photo,own,activeSquare} = metrics;
      const areaWidth = narrow ? width : width * (opened ? .45 : .70);
      const areaHeight = narrow ? 335 : mainHeight;
      const itemWidth = narrow ? Math.min(areaWidth * .59, 410) : areaWidth * .52;
      const itemHeight = areaHeight * .9;
      if (!itemWidth || !itemHeight) return rank * 5.22;
      const scale = Math.max(.3, 1 - rank * .075);
      const xShift = photo ? 50 : own ? 35.19 : 0;
      const yShift = own ? 15.66 : 0;
      const itemX = (7.7 + rank * 11.73 + xShift) * itemWidth / 100 + itemWidth / 2 + (opened ? -.04 * areaWidth : 0);
      const railX = (photo ? (opened && narrow ? .41 : .48) : opened ? (narrow ? .15 : .20) : .22) * areaWidth;
      const lift = narrow ? Math.min(30, Math.max(5, viewportWidth >= 701 ? 117.5 - .125 * viewportWidth : .12 * viewportWidth - 41)) : 0;
      let railY = .05 * areaHeight;
      if (opened && narrow && !photo) railY = (activeSquare || own ? -.05 * areaHeight : .05 * areaHeight) + (own ? 5 : -lift);
      else if (opened && !narrow && activeSquare) railY = 0;
      const angle = (opened ? 38 : 26) * Math.PI / 180;
      const hookWidth = narrow ? Math.min(.56 * itemWidth, 168) : .56 * itemWidth;
      const hookHeight = hookWidth * 2 / 3;
      const hookTop = (node.classList.contains('is-square-image') ? (opened ? 0 : .05) : .08) * itemHeight;
      const railAtHook = railY + 1 + Math.tan(angle) * (itemX - railX);
      return (railAtHook - scale * (hookTop + .23 * hookHeight)) * 100 / itemHeight - yShift;
    }

    function applyRackAlignment(opened) {
      const product = products[position];
      if (!product) return;
      const metrics = rackMetrics(product);
      nodes.forEach(node => {
        if (node.getAttribute('aria-hidden') !== 'false') return;
        const rank = Number(node.dataset.rackRank || 0);
        const y = alignedRackY(node, rank, metrics, opened);
        node.style.setProperty('--y', `${y}%`);
      });
    }

    // Viewport breakpoints and stage width can change independently inside a theme container.
    let previousStageWidth = surface.clientWidth;
    let previousViewportWidth = window.innerWidth;
    let resizeFrame = null;
    const queueAlignment = () => {
      const width = surface.clientWidth;
      const viewportWidth = window.innerWidth;
      if (width === previousStageWidth && viewportWidth === previousViewportWidth) return;
      previousStageWidth = width;
      previousViewportWidth = viewportWidth;
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => { resizeFrame = null; applyRackAlignment(surface.classList.contains('is-open')); });
    };
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(queueAlignment);
      observer.observe(surface);
      lifecycle.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
    window.addEventListener('resize', queueAlignment, listener);
    lifecycle.signal.addEventListener('abort', () => cancelAnimationFrame(resizeFrame), { once: true });

    function finishExit(node) {
      if (!node.classList.contains('is-exiting')) return;
      node.classList.remove('is-exiting', 'has-shadow', 'is-photo-exit', 'is-photo-opening-exit');
      node.hidden = true;
      stopFabricSway(node.querySelector('.nla-rack__fabric'));
    }

    function select(index, direction = 'forward') {
      if (!products.length) return;
      const previousPosition = position;
      const nextPosition = (index + products.length) % products.length;
      const changed = hasSelected && nextPosition !== position;
      const interruptedDetails = surface.classList.contains('is-detail-transitioning');
      const wasOwnHanger = surface.classList.contains('is-own-hanger');
      let returning = null;
      if (changed && nodes[nextPosition]?.classList.contains('is-exiting')) {
        // Freeze the displayed state before retargeting an in-flight departure.
        // WebKit otherwise can advance that transition while selection is updated.
        returning = nodes[nextPosition];
        const current = getComputedStyle(returning);
        const transform = current.transform, opacity = current.opacity;
        returning.style.transition = 'none';
        returning.style.transform = transform;
        returning.style.opacity = opacity;
        void returning.offsetWidth;
      }
      if (changed) { clearTimeout(detailTransitionTimer); detailTransitionTimer = null; surface.classList.remove('is-detail-transitioning'); }
      const selectedOldRank = Number(nodes[nextPosition]?.dataset.rackRank || 0);
      if (changed) {
        clearTimeout(exitTimer);
        exitTimer = null;
        nodes.forEach((node, n) => {
          // A quick reversal can select a garment that is still fading out. Keep
          // its rendered position so it can return instead of entering afresh.
          if (n === nextPosition && node.classList.contains('is-exiting')) {
            node.classList.remove('is-exiting', 'is-photo-exit', 'is-photo-opening-exit');
          }
          else finishExit(node);
        });
      }
      if (nextPosition !== position && activeUpgrade) activeUpgrade.cancel();
      position = nextPosition;
      hasSelected = true;
      const product = products[position];
      const ranks = new Map();
      let nextBackground = null;
      for (let d = 0; d < products.length; d++) {
        const n = (position + d) % products.length;
        if ((products[n].imageKind !== 'photo' && !products[n].ownHanger) || d === 0) {
          if (ranks.size < 10) ranks.set(n, ranks.size);
          else { nextBackground = n; break; }
        }
      }
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const narrow = window.matchMedia?.('(max-width: 900px)').matches;
      const correctEarlyClosing = closingCorrectionTimer === null && changed && interruptedDetails && wasOwnHanger && product.imageKind !== 'photo' && !surface.classList.contains('is-open') && narrow && !reducedMotion && surface.clientWidth >= 600 && performance.now() - detailTransitionStartedAt < 180;
      // The supplementary hook on the sequinned top follows the rail's destination
      // when a mobile detail-opening transition is interrupted immediately.
      const interruptedOwnSlope = interruptedDetails && narrow && wasOwnHanger && surface.classList.contains('is-open') ? Math.tan(38 * Math.PI / 180) : null;
      // Keep the number of moving, non-interactive departure layers bounded.
      const maxForwardExits = narrow ? 1 : 2;
      const departureGeometry = new Map();
      let railSlope = 0;
      let stageGeometry = null;
      if (changed && !reducedMotion && typeof DOMMatrixReadOnly === 'function') {
        const rail = get('area').querySelector('.nla-rack__rail');
        const railStyle = getComputedStyle(rail);
        const pseudo = getComputedStyle(rail, '::before');
        if (pseudo.content !== 'none' && Number.isFinite(parseFloat(pseudo.rotate))) railSlope = Math.tan(parseFloat(pseudo.rotate) * Math.PI / 180);
        else {
          const matrix = new DOMMatrixReadOnly(railStyle.transform === 'none' ? undefined : railStyle.transform);
          railSlope = Math.tan(Math.atan2(matrix.b, matrix.a));
        }
        const current = nodes[previousPosition];
        if (current) {
          const bounds = current.getBoundingClientRect();
          const hook = current.querySelector('.nla-rack__hanger').getBoundingClientRect();
          const transform = getComputedStyle(current).transform;
          const matrix = new DOMMatrixReadOnly(transform === 'none' ? undefined : transform);
          const scale = Math.abs(matrix.a) || 1;
          const oldRaised = surface.classList.contains('is-square-active') || (narrow && surface.classList.contains('is-own-hanger'));
          const nextRaised = (product.imageKind === 'cutout' && !product.ownHanger && nodes[nextPosition].classList.contains('is-square-image')) || (narrow && product.ownHanger);
          const railOffset = surface.classList.contains('is-open') ? (Number(nextRaised) - Number(oldRaised)) * (narrow ? .12 : .05) * get('area').offsetHeight : 0;
          stageGeometry = {width:current.offsetWidth, height:current.offsetHeight, hookY:hook.width ? (hook.top - bounds.top + hook.height * .2) / scale : current.offsetHeight * .1, railOffset};
        }
        nodes.forEach((node, n) => {
          if (ranks.has(n) || node.hidden || node.getAttribute('aria-hidden') !== 'false' || (direction !== 'backward' && Number(node.dataset.rackRank || 0) >= maxForwardExits)) return;
          const transform = getComputedStyle(node).transform;
          const matrix = new DOMMatrixReadOnly(transform === 'none' ? undefined : transform);
          const bounds = node.getBoundingClientRect();
          const hook = node.querySelector('.nla-rack__hanger').getBoundingClientRect();
          const scale = Math.abs(matrix.a) || 1;
          departureGeometry.set(n, {x:matrix.e, y:matrix.f, scale, width:node.offsetWidth, height:node.offsetHeight, hookY:hook.width ? (hook.top - bounds.top + hook.height * .2) / scale : 0});
        });
      }
      surface.classList.toggle('is-photo', product.imageKind === 'photo');
      surface.classList.toggle('is-own-hanger', Boolean(product.ownHanger));
      const alignmentMetrics = rackMetrics(product);
      const opened = surface.classList.contains('is-open');
      const exitingNodes = [];
      nodes.forEach((node, n) => {
        if (!changed && node.classList.contains('is-exiting')) return;
        const shown = ranks.has(n);
        const wasHidden = node.hidden;
        const wasShown = !wasHidden && node.getAttribute('aria-hidden') === 'false';
        const oldRank = Number(node.dataset.rackRank || 0);
        const exiting = changed && !shown && wasShown && !reducedMotion && (direction === 'backward' || oldRank < maxForwardExits);
        const renderable = shown || exiting;
        const rank = ranks.get(n) ?? -1;
        const exitToRight = exiting && direction === 'backward';
        const exitX = exitToRight ? 7.7 + 10 * 11.73 : -73 - Math.max(0, selectedOldRank - oldRank - 1) * 11.73;
        const exitScale = exitToRight ? .3 : 1;
        const geometry = departureGeometry.get(n);
        // Follow the rendered rail while accounting for the hook's change in scale.
        const exitRailShift = narrow ? 0 : stageGeometry?.railOffset || 0;
        const exitSlope = interruptedOwnSlope ?? railSlope;
        let exitY = geometry?.width && geometry.height ? (geometry.y + (exitX * geometry.width / 100 - geometry.x) * exitSlope - (exitScale - geometry.scale) * geometry.hookY - exitRailShift) * 100 / geometry.height : exitToRight ? 10 * 5.22 : 0;
        const photoExit = exitToRight && wasOwnHanger && product.imageKind === 'photo' && geometry?.width && geometry.height && alignmentMetrics;
        if (photoExit) {
          // The photo layout moves the rail. Aim the outgoing hook at its new
          // position, using the same dimensions and slope as the CSS layout.
          const areaWidth = narrow ? alignmentMetrics.width : alignmentMetrics.width * (opened ? .45 : .70);
          const areaHeight = narrow ? 335 : alignmentMetrics.mainHeight;
          const itemWidth = narrow ? Math.min(areaWidth * .59, 410) : areaWidth * .52;
          const targetRailX = (narrow && opened ? .41 : .48) * areaWidth;
          const targetHookX = (exitX / 100 + .5) * itemWidth + (opened ? -.04 * areaWidth : 0);
          const targetRailY = .05 * areaHeight + 1, targetSlope = Math.tan((opened ? 38 : 26) * Math.PI / 180);
          const targetHookY = geometry.hookY - .057 * (itemWidth - geometry.width);
          exitY = (targetRailY + (targetHookX - targetRailX) * targetSlope - exitScale * targetHookY) * 100 / (areaHeight * .9);
        }
        node.style.setProperty('--x', `${shown ? 7.7 + rank * 11.73 : exitX}%`);
        const alignedY = shown ? alignedRackY(node, rank, alignmentMetrics, opened) : exitY;
        node.style.setProperty('--y', `${alignedY}%`);
        node.style.setProperty('--scale', String(shown ? Math.max(.3, 1 - rank * .075) : exitScale));
        node.style.setProperty('--opacity', shown ? '1' : '0');
        node.style.setProperty('--z', String(shown ? 15 - rank : exitToRight ? 4 : 25 - oldRank));
        if (shown && wasHidden && changed) {
          const fromLeft = direction === 'backward' && rank === 0;
          const xShift = rank > 0 ? product.imageKind === 'photo' ? 50 : product.ownHanger ? 35.19 : 0 : 0;
          const yShift = rank > 0 && product.ownHanger ? 15.66 : 0;
          const entryX = fromLeft ? -73 : 7.7 + 10 * 11.73 + xShift;
          const entryScale = fromLeft ? 1 : .3;
          const finalScale = Math.max(.3, 1 - rank * .075);
          const finalX = 7.7 + rank * 11.73 + xShift;
          const finalY = alignedY + yShift;
          const entryY = stageGeometry?.width && stageGeometry.height ? (finalY * stageGeometry.height / 100 + (entryX - finalX) * stageGeometry.width / 100 * railSlope - (entryScale - finalScale) * stageGeometry.hookY + stageGeometry.railOffset) * 100 / stageGeometry.height : fromLeft ? 0 : 10 * 5.22;
          node.style.setProperty('--entry-x', `${entryX}%`);
          node.style.setProperty('--entry-y', `${entryY}%`);
          node.style.setProperty('--entry-scale', String(entryScale));
        } else {
          node.style.removeProperty('--entry-x');
          node.style.removeProperty('--entry-y');
          node.style.removeProperty('--entry-scale');
        }
        node.hidden = !renderable;
        node.tabIndex = shown ? 0 : -1;
        node.setAttribute('aria-hidden', String(!shown));
        node.classList.toggle('is-active', n === position);
        node.classList.toggle('is-exiting', exiting);
        node.classList.toggle('is-photo-exit', Boolean(photoExit));
        node.classList.toggle('is-photo-opening-exit', Boolean(photoExit && interruptedDetails && opened && narrow));
        if (exiting) exitingNodes.push(node);
        node.classList.toggle('has-shadow', shown && rank >= 0 && rank < 3);
        if (shown) node.dataset.rackRank = String(rank);
        const image = node.querySelector('.nla-rack__shirt');
        const adjacent = n === (position + 1) % products.length || n === (position - 1 + products.length) % products.length;
        const upcomingBackground = n === nextBackground;
        if ((renderable || adjacent || upcomingBackground) && products[n].image) {
          const desired = stageImageUrl(products[n], n === position && node.dataset.highResolutionFailed !== 'true');
          if (!image.getAttribute('src')) image.src = desired;
          else if (n === position && desired !== products[n].image && image.src !== desired) upgradeShirtImage(node, image, products[n], n, desired);
        }
      });
      if (correctEarlyClosing) {
        nodes.forEach((node, n) => { if (ranks.has(n)) node.classList.add('is-closing-adjusted'); });
        closingCorrectionTimer = setTimeout(cancelClosingCorrection, 500);
      }
      if (returning) {
        // Commit the frozen frame with transitions enabled, then release it to
        // the regular stylesheet so the product moves back from that position.
        returning.style.removeProperty('transition');
        void returning.offsetWidth;
        returning.style.removeProperty('transform');
        returning.style.removeProperty('opacity');
      }
      // The departing item is fully transparent after 550 ms, including the
      // slower photo transition; clean it up after that and handle cancellation.
      if (exitingNodes.length) exitTimer = setTimeout(() => { exitingNodes.forEach(finishExit); exitTimer = null; }, 750);
      syncActiveImageFormat();
      get('title').textContent = product.title;
      get('type').textContent = product.type;
      get('price').textContent = product.price;
      get('position').textContent = `${position + 1} / ${products.length}`;
      updateImageStatus();
      get('link').href = product.url;
      get('link').hidden = false;
      open.disabled = false;
      if (surface.classList.contains('is-open')) { renderGallery(product); queueFacts(product); }
      if (changed) swayClothes(direction === 'backward' ? -1 : 1); else revealBreeze();
    }

    previous.addEventListener('click', () => select(position - 1, 'backward'), listener);
    next.addEventListener('click', () => select(position + 1), listener);
    open.addEventListener('click', openDetails, listener);
    close.addEventListener('click', () => closeDetails(), listener);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && surface.classList.contains('is-open')) closeDetails();
    }, listener);
    items.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch' && event.isPrimary) touch = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, listener);
    items.addEventListener('pointerup', event => {
      if (!touch || touch.id !== event.pointerId) return;
      const dx = event.clientX - touch.x, dy = event.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        suppressClick = true;
        select(position + (dx < 0 ? 1 : -1), dx < 0 ? 'forward' : 'backward');
        clearTimeout(suppressTimer);
        suppressTimer = setTimeout(() => { suppressClick = false; }, 0);
      }
    }, listener);
    items.addEventListener('pointercancel', () => { touch = null; }, listener);
    lifecycle.signal.addEventListener('abort', () => { clearTimeout(suppressTimer); clearTimeout(exitTimer); clearTimeout(detailTransitionTimer); cancelClosingCorrection(); surface.classList.remove('is-detail-transitioning'); }, { once: true });

    async function request(url) {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      const timer = setTimeout(cancel, 15000);
      lifecycle.signal.addEventListener('abort', cancel, { once: true });
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Section request failed');
        return await response.text();
      } finally {
        clearTimeout(timer);
        lifecycle.signal.removeEventListener('abort', cancel);
      }
    }

    async function load() {
      const all = [], seen = new Set();
      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      const initial = window.location.search === '' ? root.querySelector('[data-rack-page]') : null;
      let pages = 1;
      for (let page = 1; page <= pages; page++) {
        let data = page === 1 && Number(initial?.dataset.page) === 1 ? initial : null;
        if (!data) {
          const url = new URL(root.dataset.collectionUrl, window.location.origin);
          if (/^[0-9]{1,20}$/.test(previewThemeId || '')) url.searchParams.set('preview_theme_id', previewThemeId);
          url.searchParams.set('section_id', root.dataset.sectionId);
          url.searchParams.set('page', String(page));
          const html = new DOMParser().parseFromString(await request(url), 'text/html');
          if (lifecycle.signal.aborted) return;
          data = html.querySelector('[data-rack-page]');
        }
        if (!data || Number(data.dataset.page) !== page) throw new Error('Unexpected pagination response');
        pages = Number(data.dataset.pages);
        if (!Number.isInteger(pages) || pages < 0 || pages > 100) throw new Error('Unexpected page count');
        for (const node of data.querySelectorAll('[data-rack-product]')) {
          const p = node.dataset;
          if (!p.id || !p.title || !p.url || seen.has(p.id)) continue;
          seen.add(p.id);
          let variants = [];
          try { const parsed = JSON.parse(p.variants || '[]'); if (Array.isArray(parsed)) variants = parsed.filter(value => typeof value === 'string' && value.trim()).slice(0, 12); } catch (_) { /* Omit malformed optional detail data. */ }
          all.push({id:p.id,title:p.title,type:p.type || '',url:p.url,price:p.price || '',image:p.image || '',fallbackImage:p.fallbackImage || '',imageKind:p.imageKind || 'photo',imageShape:p.imageShape || 'unknown',ownHanger:p.ownHanger === 'true',gallery:(p.gallery || '').split('|').filter(Boolean),description:p.description || '',variants});
        }
      }
      products = all;
      get('count').textContent = String(products.length);
      if (!products.length) { get('title').textContent = root.dataset.empty; return; }
      nodes = products.map(createItem);
      previous.disabled = next.disabled = products.length < 2;
      select(0);
    }
    load().catch(() => { if (!lifecycle.signal.aborted && root.isConnected) get('title').textContent = root.dataset.error; });
  }

  function boot(scope) {
    if (scope.matches?.('[data-nla-rack]')) initialize(scope);
    scope.querySelectorAll('[data-nla-rack]').forEach(initialize);
  }
  function dispose(scope) {
    const roots = [...scope.querySelectorAll('[data-nla-rack]')];
    if (scope.matches?.('[data-nla-rack]')) roots.push(scope);
    roots.forEach(root => { active.get(root)?.abort(); active.delete(root); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => dispose(event.target));
  window.addEventListener('pagehide', () => dispose(document));
  window.addEventListener('pageshow', event => { if (event.persisted) boot(document); });
})();
